import { useLoadingStore } from '@/stores/loading.ts'
import { useRouter } from 'vue-router'
import { useToastStore } from '@/stores/toast.ts'
import { useUserStore } from '@/stores/user.ts'

export default () => {
  // Stores et dépendances globales
  const loadingStore = useLoadingStore()
  const router = useRouter()
  const toastStore = useToastStore()
  const userStore = useUserStore()

  /**
   * Envoie une requête HTTP générique.
   *
   * - Affiche le loader si demandé.
   * - Retourne directement la réponse si succès.
   * - En cas d'erreur HTTP, construit un objet d'erreur structuré :
   *   `{ status, message, code? }`
   * - En cas d'erreur réseau (déconnexion, DNS, CORS…), rejette :
   *   `{ network: true, original: err }`
   *
   * @param url - URI à appeler (relative à VITE_API_URL).
   * @param options - Options du fetch.
   * @param loading - Active/désactive l'indicateur visuel de chargement.
   * @param successDetail - Message toast affiché en cas de succès.
   * @param errorDetail - Message toast par défaut si le serveur n’en fournit pas.
   * @param life - Durée d’affichage du toast.
   */
  const request = async (
    url: string,
    options: object,
    loading: boolean = false,
    successDetail: string | undefined = undefined,
    errorDetail: string | undefined = undefined,
    life: number | undefined = 5000,
  ) => {
    try {
      // Affiche le loader si demandé.
      if (loading) loadingStore.start()

      // Construit et exécute la requête HTTP.
      const baseUrl = import.meta.env.VITE_API_URL
      const response = await fetch(`${baseUrl}/${url}`, {
        headers: {
          Accept: 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        ...options,
      })

      // Succès HTTP
      if (response.ok) {
        // Si demandé, on affiche un message de succès
        if (successDetail !== undefined) toastStore.success(successDetail, life)
        return response
      }

      // Tentative de parser un JSON d’erreur si disponible
      let jsonBody = null
      try {
        jsonBody = await response.json()
      } catch {
        // Body non-JSON ou vide
        jsonBody = null
      }

      // Message d’erreur générique par défaut
      let detail = errorDetail || "Une erreur s'est produite."

      // Gestion des statuts HTTP
      switch (response.status) {
        case 400:
          detail = 'La requête est incorrecte.'
          break

        case 401:
          userStore.resetUtilisateur()
          detail = "Vous n'êtes plus connecté."
          // On déconnecte l'utilisateur et on le redirige vers la page d'accueil
          await router.push({ name: 'connexion' })
          break

        case 403:
          detail = "Vous n'avez pas les autorisations nécessaires."
          // On redirige l'utilisateur vers la page d'accueil
          await router.push({ name: 'accueil' })
          break

        case 404:
          detail = 'Ressource introuvable.'
          break

        case 500:
          detail = "Une erreur système s'est produite."
          break

        case 502:
          detail = 'Le serveur est en maintenance.'
          // On redirige l'utilisateur vers la page de maintenance
          // TODO: Créer la page de maintenance
          await router.push({ name: 'maintenance' })
          break
      }

      // Extraction du message d’erreur clair
      const message = jsonBody?.detail?.message || jsonBody?.detail || detail

      // Extraction éventuelle d’un code d’erreur
      const code = jsonBody?.detail?.code

      // Affichage du toast d’erreur
      toastStore.danger(message, life)

      // Rejet structuré de l'erreur
      return Promise.reject({
        status: response.status,
        message,
        ...(code && { code }),
      })
    } catch (err) {
      // Erreur réseau (aucune réponse HTTP reçue)
      toastStore.danger('Une erreur réseau est survenue. Vérifiez votre connexion Internet.', life)

      return Promise.reject({ network: true, original: err })
    } finally {
      // Masque le loader si activé
      if (loading) loadingStore.stop()
    }
  }

  /**
   * Effectue une requête GET et retourne le JSON.
   *
   * @param url - URI relative.
   * @param loading - Active le loader.
   * @param fallback - Donnée retournée si une erreur survient.
   * @param successDetail - Message de succès optionnel.
   * @param errorDetail - Message d’erreur par défaut.
   * @param life - Durée du toast.
   */
  const get = async (
    url: string,
    loading: boolean = false,
    fallback: object | undefined | null = {},
    successDetail?: string,
    errorDetail?: string,
    life?: number,
  ) => {
    try {
      const response = await request(
        url,
        { method: 'GET' },
        loading,
        successDetail,
        errorDetail,
        life,
      )
      return await response.json()
    } catch {
      return Promise.reject(fallback)
    }
  }

  /**
   * Effectue une requête POST et retourne le JSON.
   */
  const post = async (
    url: string,
    data: object = {},
    loading: boolean = false,
    successDetail?: string,
    errorDetail?: string,
    life?: number,
  ) => {
    try {
      const response = await request(
        url,
        { body: JSON.stringify(data), method: 'POST' },
        loading,
        successDetail,
        errorDetail,
        life,
      )
      return await response.json()
    } catch (error) {
      return Promise.reject(error)
    }
  }

  /**
   * Effectue une requête POST et retourne un Blob.
   */
  const blob = async (
    url: string,
    data: object = {},
    loading: boolean = false,
    successDetail?: string,
    errorDetail?: string,
    life?: number,
  ) => {
    try {
      const response = await request(
        url,
        { body: JSON.stringify(data), method: 'POST' },
        loading,
        successDetail,
        errorDetail,
        life,
      )
      return await response.blob()
    } catch {
      return new Blob()
    }
  }

  /**
   * Effectue une requête POST avec FormData et retourne le JSON.
   */
  const formData = async (
    url: string,
    data: object = {},
    loading: boolean = false,
    successDetail?: string,
    errorDetail?: string,
    life?: number,
  ) => {
    try {
      const response = await request(
        url,
        { body: data, method: 'POST', headers: { Accept: '*/*' } },
        loading,
        successDetail,
        errorDetail,
        life,
      )
      return await response.json()
    } catch {
      return {}
    }
  }

  /**
   * Effectue une requête PUT et retourne le JSON.
   */
  const put = async (
    url: string,
    data: object = {},
    loading: boolean = false,
    fallback: object | undefined | null = {},
    successDetail?: string,
    errorDetail?: string,
    life?: number,
  ) => {
    try {
      const response = await request(
        url,
        { body: JSON.stringify(data), method: 'PUT' },
        loading,
        successDetail,
        errorDetail,
        life,
      )
      return await response.json()
    } catch {
      return Promise.reject(fallback)
    }
  }

  /**
   * Effectue une requête DELETE et retourne le JSON.
   */
  const deleteApi = async (
    url: string,
    data: object = {},
    loading: boolean = false,
    fallback: object | undefined | null = {},
    successDetail?: string,
    errorDetail?: string,
    life?: number,
  ) => {
    try {
      const response = await request(
        url,
        { body: JSON.stringify(data), method: 'DELETE' },
        loading,
        successDetail,
        errorDetail,
        life,
      )
      return await response.json()
    } catch {
      return Promise.reject(fallback)
    }
  }

  return {
    blob,
    get,
    formData,
    put,
    post,
    delete: deleteApi,
  }
}
